import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth strategy:
//   1. Primary: HttpOnly cookies (most secure, no JS access to token)
//   2. Fallback: Authorization: Bearer header from sessionStorage
//      Cookies fail when the frontend lives on a custom domain (e.g. deal-desk.xyz)
//      and the backend is on a different root (e.g. *.emergentagent.com) — iOS
//      Safari/Chrome's ITP blocks them as third-party. sessionStorage is cleared
//      on tab close, dramatically smaller XSS surface than localStorage.
//
// Both are sent on every request; the backend (server.py get_current_user) accepts
// whichever arrives first.

const TOKEN_KEY = 'dd_session_token';

// One-time cleanup: purge any legacy persistent tokens that older builds stored
try {
  localStorage.removeItem('dd_access_token');
  localStorage.removeItem('dd_refresh_token');
} catch {}

const getStoredToken = () => {
  try { return sessionStorage.getItem(TOKEN_KEY); } catch { return null; }
};

const storeToken = (token) => {
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {}
};

// Make withCredentials the default — every axios request sends cookies
axios.defaults.withCredentials = true;

// Read a cookie value by name (used to echo csrf_token in the X-CSRF-Token header)
const readCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// Attach Authorization: Bearer header from sessionStorage on every outbound request.
// Backend get_current_user falls back to this header when the cookie is missing.
// Also echo csrf_token cookie -> X-CSRF-Token header on mutating cookie-auth requests.
axios.interceptors.request.use((config) => {
  const token = getStoredToken();
  config.headers = config.headers || {};
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const method = (config.method || 'get').toUpperCase();
  if (UNSAFE_METHODS.has(method) && !config.headers['X-CSRF-Token']) {
    const csrf = readCookie('csrf_token');
    if (csrf) config.headers['X-CSRF-Token'] = csrf;
  }
  return config;
});

// Handle 402 trial_expired globally — bounce to billing page
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    if (status === 402 && detail === 'trial_expired') {
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/billing')) {
        // Use a session flag so we don't infinite-loop if billing endpoints somehow 402
        if (!window.__trial_redirect_in_flight) {
          window.__trial_redirect_in_flight = true;
          setTimeout(() => { window.__trial_redirect_in_flight = false; }, 2000);
          window.location.assign('/billing');
        }
      }
    }
    return Promise.reject(error);
  }
);

// Setup axios interceptor for automatic token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve();
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // Skip refresh for auth endpoints themselves
    if (originalRequest.url?.includes('/auth/login') ||
        originalRequest.url?.includes('/auth/register') ||
        originalRequest.url?.includes('/auth/refresh') ||
        originalRequest.url?.includes('/auth/logout')) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => axios(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshRes = await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
        // Capture the new access token for the Bearer fallback (mobile / cross-origin)
        if (refreshRes.data?.access_token) storeToken(refreshRes.data.access_token);
        processQueue(null);
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        storeToken(null);
        window.dispatchEvent(new Event('auth-expired'));
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();

    const handleAuthExpired = () => setUser(false);
    window.addEventListener('auth-expired', handleAuthExpired);
    return () => window.removeEventListener('auth-expired', handleAuthExpired);
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (error) {
      // Try refresh token before giving up
      try {
        const refreshRes = await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
        if (refreshRes.data?.access_token) storeToken(refreshRes.data.access_token);
        const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
        setUser(data);
      } catch {
        storeToken(null);
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      // Persist access token for the Bearer fallback (mobile / cross-origin domains
      // where cookies are blocked by iOS ITP). Cookie path still works on desktop.
      if (data.access_token) storeToken(data.access_token);
      setUser(data);
      return { success: true };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
    }
  };

  const register = async (email, password, name, role = 'rep', inviteToken = null) => {
    try {
      const payload = { email, password, name, role };
      if (inviteToken) payload.invite_token = inviteToken;
      const { data } = await axios.post(
        `${API_URL}/api/auth/register`,
        payload,
        { withCredentials: true }
      );
      if (data.access_token) storeToken(data.access_token);
      setUser(data);
      return { success: true };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    storeToken(null);
    setUser(false);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
